import styled from "@emotion/styled";

import { MonthlyEarnings } from "@/components/pages_public/sales-comp/utils/types";

const TooltipTitle = styled.h4<{ large?: boolean }>`
  margin-bottom: ${(props) => (props.large ? "0.75rem" : "0.5rem")};
  font-weight: 600;
  font-size: ${(props) => (props.large ? "1.125rem" : "inherit")};
`;

const AcceleratorTable = styled.table`
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
`;

const AcceleratorHeaderRow = styled.tr`
  border-bottom: 1px solid #4b5563;
`;

const AcceleratorHeaderCell = styled.th<{ align?: string; green?: boolean }>`
  padding: 0.5rem;
  font-weight: 700;
  text-align: ${(props) => props.align || "left"};
  color: ${(props) => (props.green ? "#22c55e" : "inherit")};
`;

const AcceleratorRow = styled.tr`
  border-bottom: 1px solid #374151;
`;

const AcceleratorCell = styled.td<{
  align?: string;
  blue?: boolean;
  green?: boolean;
  purple?: boolean;
  greenLight?: boolean;
  bold?: boolean;
}>`
  padding: 0.5rem;
  text-align: ${(props) => props.align || "left"};
  color: ${(props) =>
    props.blue
      ? "#60a5fa"
      : props.green
        ? "#34d399"
        : props.purple
          ? "#c084fc"
          : props.greenLight
            ? "#86efac"
            : "inherit"};
  font-weight: ${(props) => (props.bold ? "700" : "normal")};
`;

const AcceleratorTotalRow = styled.tr`
  border-top: 2px solid #9ca3af;
  border-bottom: 2px solid #9ca3af;
  background-color: #1f2937;
  font-weight: 700;
  color: white;
`;

const AcceleratorFooter = styled.div`
  margin-top: 1rem;
  border-top: 1px solid #4b5563;
  padding-top: 0.5rem;
  font-size: 0.875rem;
  color: #d1d5db;
  width: 100%;

  & > div:first-of-type {
    margin-bottom: 0.25rem;
    font-weight: 500;
  }
`;

const TiersList = styled.div`
  white-space: normal;
  word-wrap: break-word;
  margin-top: 8px;
  margin-left: 12px;
  line-height: 1.5;
`;

interface AcceleratorTooltipProps {
  month: MonthlyEarnings;
  accelerators: { threshold: number; multiplier: number }[];
  getAdjustedCommissionRate: (rampPercentage: number) => number;
}

export function AcceleratorTooltip({
  month,
  accelerators,
  getAdjustedCommissionRate,
}: AcceleratorTooltipProps) {
  return (
    <div>
      <TooltipTitle large>Accelerator Explanation</TooltipTitle>

      <AcceleratorTable>
        <thead>
          <AcceleratorHeaderRow>
            <AcceleratorHeaderCell align="left">Tier</AcceleratorHeaderCell>
            <AcceleratorHeaderCell align="right">
              Revenue in Tier
            </AcceleratorHeaderCell>
            <AcceleratorHeaderCell align="right">
              Standard Commission
            </AcceleratorHeaderCell>
            <AcceleratorHeaderCell align="right" green>
              Accelerator Bonus
            </AcceleratorHeaderCell>
          </AcceleratorHeaderRow>
        </thead>
        <tbody>
          {month.tierInfo.tierPortions.map(
            (portion, idx) =>
              portion > 0 && (
                <AcceleratorRow key={idx}>
                  <AcceleratorCell
                    blue={idx === 0}
                    green={idx === 1}
                    purple={idx === 2}
                  >
                    Tier {idx + 1}
                  </AcceleratorCell>
                  <AcceleratorCell align="right">
                    ${Math.round(portion).toLocaleString()}
                  </AcceleratorCell>
                  <AcceleratorCell align="right">
                    $
                    {Math.round(
                      (portion *
                        getAdjustedCommissionRate(month.rampPercentage)) /
                        100,
                    ).toLocaleString()}
                  </AcceleratorCell>
                  <AcceleratorCell align="right" green bold>
                    +$
                    {Math.round(
                      (portion *
                        getAdjustedCommissionRate(month.rampPercentage) *
                        (accelerators[idx]?.multiplier - 1 || 0)) /
                        100,
                    ).toLocaleString()}
                  </AcceleratorCell>
                </AcceleratorRow>
              ),
          )}
          <AcceleratorTotalRow>
            <AcceleratorCell>Total</AcceleratorCell>
            <AcceleratorCell align="right">
              ${Math.round(month.commissionableRevenue).toLocaleString()}
            </AcceleratorCell>
            <AcceleratorCell align="right">
              ${Math.round(month.baseCommission).toLocaleString()}
            </AcceleratorCell>
            <AcceleratorCell align="right" greenLight bold>
              +${Math.round(month.acceleratorAmount).toLocaleString()}
            </AcceleratorCell>
          </AcceleratorTotalRow>
        </tbody>
      </AcceleratorTable>

      <AcceleratorFooter>
        <div>Accelerator Thresholds</div>
        <TiersList>
          {accelerators.map((accelerator, idx) => {
            const threshold = month.tierInfo.tierThresholds[idx];
            const nextThreshold = month.tierInfo.tierThresholds[idx + 1];

            if (idx === accelerators.length - 1) {
              return (
                <div key={idx}>
                  • Tier {idx + 1} ({accelerator.multiplier}x multiplier): $
                  {Math.round(threshold).toLocaleString()}+
                </div>
              );
            } else {
              return (
                <div key={idx}>
                  • Tier {idx + 1} ({accelerator.multiplier}x multiplier): $
                  {Math.round(threshold).toLocaleString()} to $
                  {Math.round(nextThreshold).toLocaleString()}
                  <br />
                </div>
              );
            }
          })}
        </TiersList>
      </AcceleratorFooter>
    </div>
  );
}
